import { Disclosure, Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { transactionsFilters } from '@/modules/issuer-dashboard/components/holder-tabs/TransactionsTab.tsx';
import { useIssuerAddActivityMenuOptions } from '@/modules/issuer-dashboard/hooks/useIssuerAddActivityMenuOptions.ts';
import { useIssuerGenerateRedemptionPdfFormMutation } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import {
  Button,
  DropdownMenu,
  FilterItem,
  RoundBadge,
  Spinner,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { FiltersIcon, InfoCircleWhiteIcon } from 'ui/icons';
import { downloadFile, fixNameEnum, openInNewTab } from 'ui/utils';
import { ShareholderExtendResponse, SortedBy, TransactionStatus, TransactionType } from 'api';

const statusOptions = getSelectOptionByEnum<TransactionStatus>(TransactionStatus);
const sortOptions = getSelectOptionByEnum<SortedBy>(SortedBy);

interface HolderTransactionTabFiltersProps {
  filters: transactionsFilters;
  setFilters: Dispatch<SetStateAction<transactionsFilters>>;
  shareholder: ShareholderExtendResponse | null;
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
  isFetching?: boolean;
}

const HolderTransactionTabFilters = ({
  filters,
  setFilters,
  shareholder,
  isDeleted,
  setIsDeleted,
  isFetching = false,
}: HolderTransactionTabFiltersProps) => {
  const { sorted_by, type, status, confirmation_date_from, confirmation_date_to } = filters;
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const addActivityOptions = useIssuerAddActivityMenuOptions({
    registerId: Number(registerId),
    shareholderId: shareholder?.id,
  });

  const dateValue =
    confirmation_date_from && confirmation_date_to
      ? ([dayjs(confirmation_date_from), dayjs(confirmation_date_to)] as [Dayjs, Dayjs])
      : undefined;

  const appliedFiltersLength = Object.keys(filters).length;

  const handleChangeStatus = useCallback(
    (status: TransactionStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeType = useCallback(
    (type: TransactionType | null) => {
      setFilters(filters => (type ? { ...filters, type } : omit(filters, 'type')));
    },
    [setFilters],
  );

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              confirmation_date_from: date[0].toISOString(),
              confirmation_date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['confirmation_date_from', 'confirmation_date_to']);
      });
    },
    [setFilters],
  );

  const handleChangeSortedBy = useCallback(
    (sorted_by: SortedBy | null) => {
      setFilters(filters => (sorted_by ? { ...filters, sorted_by } : omit(filters, 'sorted_by')));
    },
    [setFilters],
  );

  const typeOptions = useMemo(() => {
    return Object.values(TransactionType).map(value => {
      if (value === TransactionType.Drp) {
        return {
          label: 'Dividend reinvestment',
          value,
        };
      }

      if (value === TransactionType.Subscription) {
        return {
          label: getRegisterTypeStrings('application', { capitalize: true }),
          value,
        };
      }

      return {
        label: fixNameEnum(value),
        value,
      };
    });
  }, [getRegisterTypeStrings]);

  const redemptionPdfFileName = useMemo(() => {
    const holderName =
      shareholder?.entity?.entity_name?.trim() || `holder-${shareholder?.id ?? ''}`;
    return `${holderName}-redemption-pdf-form-${dayjs().format('YYYY-MM-DD-HH-mm')}.pdf`;
  }, [shareholder?.entity?.entity_name, shareholder?.id]);

  const { mutate: generateRedemptionPdfForm, isPending: isGeneratingRedemptionPdfForm } =
    useIssuerGenerateRedemptionPdfFormMutation({
      onSuccess: file => {
        if (file) {
          const namedFile = new File([file], redemptionPdfFileName, {
            type: file.type || 'application/pdf',
          });
          const fileUrl = URL.createObjectURL(namedFile);
          openInNewTab(fileUrl);
          window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
          downloadFile(file, redemptionPdfFileName);
        }
      },
    });

  const handleGenerateRedemptionPdfForm = useCallback(() => {
    if (!shareholder?.entity_id) return;
    generateRedemptionPdfForm({
      filename: redemptionPdfFileName,
      entityId: shareholder?.entity_id,
    });
  }, [generateRedemptionPdfForm, shareholder?.entity_id, redemptionPdfFileName]);

  return (
    <Disclosure>
      <div className="flex gap-4 items-center justify-end flex-wrap">
        {setIsDeleted && (
          <>
            {isFetching && <Spinner size="sm" className="m-0 mt-1" />}
            <ArchiveSwitch
              active={!!isDeleted}
              onChange={active => setIsDeleted(active || undefined)}
            />
          </>
        )}
        <Button
          className="w-auto flex gap-2 items-center"
          loading={isGeneratingRedemptionPdfForm}
          disabled={!shareholder || isGeneratingRedemptionPdfForm || !shareholder?.entity_id}
          onClick={handleGenerateRedemptionPdfForm}
        >
          {/*  */}
          <span>Download Redemption PDF Form</span>
          <TooltipNew
            text={
              'You can send this PDF form to a non technical investor and ask to print, fill out and send to you. ' +
              'Once you receive a filled form, you can create a redemption transaction from Transactions page or ' +
              'from their Holder Profile page => Transactions tab using Add transaction button'
            }
          >
            <img
              alt="info"
              src={InfoCircleWhiteIcon}
              className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]"
            />
          </TooltipNew>
        </Button>
        <DropdownMenu
          items={addActivityOptions}
          button={<Button className="w-[140px]">Add transaction</Button>}
          containerClassName="w-[140px]"
          className="w-[160px] divide-white"
          placement="left-start"
        />
        <Disclosure.Button>
          <div className="w-fit flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
            <img alt="filters" src={FiltersIcon} />
            <span className="text-sm">Filters</span>
            {!!appliedFiltersLength && <RoundBadge content={appliedFiltersLength} />}
          </div>
        </Disclosure.Button>
      </div>

      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Disclosure.Panel static>
          <div className="flex flex-wrap gap-4 justify-end">
            <FilterItem
              label="Status"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              value={status}
              options={statusOptions}
              onChange={handleChangeStatus}
            />
            <FilterItem
              label="Type"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[220px]' }}
              value={type}
              options={typeOptions}
              onChange={handleChangeType}
            />
            <FilterItem
              label="Date range"
              type="date"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              onChange={handleChangeDate}
              value={dateValue}
            />
            <FilterItem
              label="Sorting"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              value={sorted_by}
              options={sortOptions}
              onChange={handleChangeSortedBy}
            />
          </div>
        </Disclosure.Panel>
      </Transition>
    </Disclosure>
  );
};

export default HolderTransactionTabFilters;
